import { useId } from 'react';
import type { Point } from '@/api/types';

/** Inline SVG sparkline: ink line with the brand's yellow area (18%) underneath. Decorative. */
export function Sparkline({ points, className, height = 40 }: { points: Point[]; className?: string; height?: number }) {
  const gid = useId();
  if (points.length < 2) {
    return <div aria-hidden className={className} style={{ height }} />;
  }
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const w = 100;
  const h = 32;
  const pad = 2;
  const xy = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = pad + (1 - (v - min) / span) * (h - pad * 2);
    return [x, y] as const;
  });
  const line = xy.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;
  const [lx, ly] = xy[xy.length - 1]!;
  return (
    <div className="relative" aria-hidden>
    <svg
      aria-hidden
      data-testid="sparkline"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className={className}
      style={{ height, width: '100%', overflow: 'visible', display: 'block' }}
    >
      <defs>
        <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--md-yellow)" stopOpacity="0.32" />
          <stop offset="100%" stopColor="var(--md-yellow)" stopOpacity="0.04" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke="var(--md-ink)" strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
      {/* The end dot is HTML so preserveAspectRatio=none doesn't squash it. */}
      <span
        className="absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-ink bg-yellow"
        style={{ left: `${lx}%`, top: `${(ly / h) * 100}%` }}
      />
    </div>
  );
}
